import {
  Family,
  FamilyChildProfile,
  FamilyParentProfile,
  ParentTrainingStatus,
  QuestInstance,
  QuestSeries,
} from "@joonapp/web-shared"
import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { AxiosResponse } from "axios"
import dayjs from "dayjs"

import { getMeTabCard } from "./coaching/api"
import { loadFamilies } from "./families/api"
import { getIncidents } from "./incidents"
import {
  getAssignedQuests,
  getCustomQuestTemplates,
  getOpenQuests,
  getParentTaskInstances,
  getQuestInstanceStats,
  getQuestLog,
  getQuestsToReview,
  getSuggestedQuestTemplates,
} from "./quests"
import { getCampaignPlans, getSubscriberInfo } from "./subscriptions"
import { getTrainings } from "./trainings"
import { getCoinCumulativeStats, getCoinTransactions } from "./transactions"
import {
  getNetworkInfo,
  loadQuestionnaires,
  getChildAuthCode,
  getParentAuthCode,
  getTherapists,
  getTherapistInvites,
  loadRecommendedQuestionnaires,
  loadFamilyMail,
  loadInventoryItems,
  getNotificationPreferences,
  loadCoachIntakeFormQuestionnaire,
  loadCoachPostSelectionQuestionnaire,
  getLocationInfo,
  loadCoachOnboardIntakeFormQuestionnaire,
} from "./user"
import { loadUser } from "./users/api"
import { useChildQuestionnaireModalStore } from "../components/modals/childQuestionnaireModal/useChildQuestionnaireModalStore"
import {
  createSurveyVariants,
  customOnboardSurveyData,
} from "../components/onboarding/customOnboardSurveyData"
import { periodDropdownOptions, QUERY_KEYS } from "../constants"
import useOnboardContext from "../hooks/useOnboardContext"
import useCareTeam from "../pages/care/useCareTeam"
import { useCoinTransactionsModalStore } from "../pages/family/coinTransactions/useTransactionsModalStore"
import { useQBDatePickerStore } from "../pages/questBoard/QuestBoardDatePicker"
import { FamilyQuerySelect, QuestionnairesInterface } from "../types"
import { QuestTemplate, QuestTemplateCategory } from "../types/quests"
import { getAllQuestions, getChildQuestions } from "../util/questionnaires"
import { useRouter } from "../util/router"
import { localStorageItems, sessionManager } from "../util/storage"
import {
  getMondayOfWeek,
  getTherapistsWithPatients,
  groupInvites,
  separateFamily,
  sortByRoutine,
} from "../util/util"

export const useQuestionnairesQuery = () => {
  const { parentSurveyVariant, childSurveyVariant, isCustomOnboarding } =
    useOnboardContext()

  return useQuery({
    queryKey: [QUERY_KEYS.QUESTIONNAIRES],
    queryFn: loadQuestionnaires,
    select: (questionnaires: QuestionnairesInterface[]) => {
      if (isCustomOnboarding) {
        const { childQuestions, parentQuestions, onboardingQuestionnaires } =
          createSurveyVariants(customOnboardSurveyData)
        return {
          childQuestions: childQuestions[childSurveyVariant],
          parentQuestions: parentQuestions[parentSurveyVariant],
          onboardingQuestionnaires,
        }
      } else {
        return getAllQuestions(questionnaires)
      }
    },
  })
}

export const useUserQuery = (resetAuthTokensOnError: boolean = false) => {
  const router = useRouter()
  const { isCreatingUser } = useOnboardContext()
  const { data: user, status: userStatus } = useQuery({
    queryKey: [QUERY_KEYS.USER],
    queryFn: loadUser,
    enabled: sessionManager.hasRefreshToken() && !isCreatingUser,
    // A 403 is deterministic (no account yet) — retrying only stalls the UI, so
    // never retry it. Other failures keep the default bounded retry.
    retry: (failureCount, error: any) =>
      error?.response?.status !== 403 && failureCount < 3,
    onError: (error: any) => {
      if (error.response?.status === 403) {
        // User does not exist in backend. Route to create account — but not
        // while we're mid-creation, where a transient 403 would bounce the user
        // out of the flow.
        if (!isCreatingUser) router.replace("/onboard")
      } else if (resetAuthTokensOnError) {
        sessionManager.clearAuthTokens()
      }
    },
  })

  return { user, userStatus }
}

// Function overloads
export function useFamilyQuery(): UseQueryResult<Family, unknown>
export function useFamilyQuery(
  select: FamilyQuerySelect.CHILDREN
): UseQueryResult<FamilyChildProfile[], unknown>
export function useFamilyQuery(
  select: FamilyQuerySelect.PARENT
): UseQueryResult<FamilyParentProfile, unknown>
export function useFamilyQuery(
  select: FamilyQuerySelect.COPARENTS
): UseQueryResult<FamilyParentProfile[], unknown>

export function useFamilyQuery(select?: FamilyQuerySelect) {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.FAMILY],
    queryFn: loadFamilies,
    select: (family: Family) => {
      const { children, parent, coparents } = separateFamily(user, family)
      const familyWithSortedProfiles = {
        ...family,
        profiles: [...children, parent, ...coparents],
      }
      switch (select) {
        case FamilyQuerySelect.CHILDREN:
          return children
        case FamilyQuerySelect.PARENT:
          return parent
        case FamilyQuerySelect.COPARENTS:
          return coparents
        default:
          return familyWithSortedProfiles
      }
    },
    enabled: !!user,
  })
}

export const useFamilyMailQuery = ({
  isUnread = false,
  childId,
}: {
  isUnread?: boolean
  childId?: number
}) => {
  const { user } = useUserQuery()

  return useQuery({
    queryKey: [QUERY_KEYS.FAMILY_MAIL, isUnread, childId],
    queryFn: () =>
      loadFamilyMail({
        userId: user?.id as number,
        childId: childId as number,
        receiverId: isUnread ? (user?.id as number) : undefined,
        isUnread,
      }),
    enabled: !!user,
  })
}

export const useInventoryGiftsQuery = () => {
  const { user } = useUserQuery()

  return useQuery({
    queryKey: [QUERY_KEYS.INVENTORY],
    queryFn: () => loadInventoryItems(user?.id as number),
    enabled: !!user,
  })
}

export const useSubscriberInfo = () => {
  const { data: family } = useFamilyQuery()

  return useQuery({
    queryKey: [QUERY_KEYS.SUBSCRIBER_INFO],
    queryFn: () => getSubscriberInfo(family!),
    enabled: !!family,
  })
}

export const useUserNetworkInfo = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.NETWORK_INFO],
    queryFn: getNetworkInfo,
  })
}

export const useUserLocationInfo = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.COUNTRY_CODE],
    queryFn: getLocationInfo,
    select: (response) => response.data,
  })
}

export const useChildAuthCodeQuery = () => {
  const { data: family } = useFamilyQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.CHILD_AUTH_CODE],
    queryFn: getChildAuthCode,
    select: (response) => response.data.results[0].code,
    enabled: !!family,
  })
}

export const useParentAuthCodeQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.PARENT_AUTH_CODE],
    queryFn: getParentAuthCode,
    select: (response) => response.data.results[0].code,
    enabled: !!user,
  })
}

export const useQuestTemplatesQuery = (
  ageGroup: string,
  templateCategory: QuestTemplateCategory,
  userId?: number
) => {
  const { user } = useUserQuery()
  const isCustomTemplate = templateCategory === QuestTemplateCategory.CUSTOM

  return useQuery({
    queryKey: [QUERY_KEYS.QUEST_TEMPLATES, ageGroup, templateCategory, userId],
    queryFn: isCustomTemplate
      ? () => getCustomQuestTemplates()
      : () =>
          getSuggestedQuestTemplates(
            templateCategory,
            ageGroup,
            userId as number
          ),
    enabled: !!user && !!userId,
    select: (response: AxiosResponse<{ results: QuestTemplate[] }>) =>
      response.data.results,
  })
}

export const useTherapistsQuery = () => {
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  return useQuery({
    queryKey: [QUERY_KEYS.THERAPISTS],
    queryFn: () => getTherapists(children!),
    select: getTherapistsWithPatients,
    enabled: !!children && children.length > 0,
  })
}

export const useTherapistInvitesQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.THERAPIST_INVITES],
    queryFn: getTherapistInvites,
    select: (response) => groupInvites(response.data.results),
    enabled: !!user,
  })
}

export const useAverageCoinsEarnedThisWeekQuery = () => {
  // this is the (coins earned this week from all children) / (number of children)
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  return useQuery({
    queryKey: [QUERY_KEYS.AVERAGE_COINS_EARNED_THIS_WEEK],
    queryFn: async () => {
      if (!children || children.length === 0) return 0

      const startDate = dayjs().subtract(7, "day").format("YYYY-MM-DD")
      const endDate = dayjs().format("YYYY-MM-DD")

      const statsPromises = children.map((child) =>
        getCoinCumulativeStats({
          userId: child.user.id,
          startDate,
          endDate,
        })
      )

      const statsResults = await Promise.all(statsPromises)
      let totalCoins = 0
      for (const stats of statsResults) totalCoins += stats.coins_earned
      return Math.floor(totalCoins / children.length)
    },
    enabled: !!children,
  })
}

export const useCoinCulumativeStatsQuery = () => {
  const { child, selectedPeriod } = useCoinTransactionsModalStore()

  return useQuery({
    queryKey: [QUERY_KEYS.COIN_CUMULATIVE_STATS, child?.id, selectedPeriod],
    queryFn: () =>
      getCoinCumulativeStats({
        userId: child?.id,
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate,
      }),
    enabled: !!child,
  })
}

export const useCoinStatsQuery = ({
  userId,
  startDate,
  endDate,
}: {
  userId: number | undefined
  startDate: string
  endDate: string
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.COIN_CUMULATIVE_STATS, userId, startDate, endDate],
    queryFn: () =>
      getCoinCumulativeStats({
        userId: userId!,
        startDate,
        endDate,
      }),
    enabled: !!userId,
  })
}

export const useCoinCulumativeStatsThisWeekQuery = (userId: number) => {
  return useQuery({
    queryKey: [
      QUERY_KEYS.COIN_CUMULATIVE_STATS,
      userId,
      periodDropdownOptions[1],
    ],
    queryFn: () =>
      getCoinCumulativeStats({
        userId,
        startDate: dayjs().subtract(7, "day").format("YYYY-MM-DD"),
        endDate: dayjs().format("YYYY-MM-DD"),
      }),
    enabled: !!userId,
  })
}

export const useCoinTransactionsQuery = () => {
  const { child, selectedPeriod } = useCoinTransactionsModalStore()

  return useQuery({
    queryKey: [QUERY_KEYS.COIN_TRANSACTIONS, child?.id, selectedPeriod],
    queryFn: () =>
      getCoinTransactions({
        userId: child?.id,
        startDate: selectedPeriod.startDate,
        endDate: selectedPeriod.endDate,
        page: 1,
      }),
    select: (response) => response?.data.results,
    enabled: !!child,
  })
}

export const useRecommendedQuestionnairesQuery = () => {
  const { childId } = useChildQuestionnaireModalStore()

  return useQuery({
    queryKey: [QUERY_KEYS.RECOMMENDATION_QUESTIONNAIRES, childId],
    queryFn: () => loadRecommendedQuestionnaires(childId as number),
    select: getChildQuestions,
    enabled: typeof childId === "number",
  })
}

export const useCoachIntakeFormQuestionnaireQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.COACH_INTAKE_FORM],
    queryFn: loadCoachIntakeFormQuestionnaire,
  })
}

export const useCoachOnboardIntakeFormQuestionnaireQuery = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.COACH_ONBOARD_INTAKE_FORM],
    queryFn: loadCoachOnboardIntakeFormQuestionnaire,
  })
}

export const useCoachPostSelectionQuestionnaireQuery = () => {
  const { user } = useUserQuery()

  return useQuery({
    queryKey: [QUERY_KEYS.COACH_POST_SELECTION],
    queryFn: loadCoachPostSelectionQuestionnaire,
    enabled: !!user,
  })
}

export const useQuestInstanceStatsQuery = (userId: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUEST_INSTANCE_STATS, userId],
    queryFn: () => getQuestInstanceStats(userId),
  })
}

export const useQuestBoardQuery = (date?: string) => {
  const { questBoardDate } = useQBDatePickerStore()
  const { data: family } = useFamilyQuery()
  const userIds = family?.profiles?.map((profile) => profile.user.id) || []

  const selectedDate = date || questBoardDate?.format("YYYY-MM-DD") || ""
  return useQuery<QuestInstance[]>({
    queryKey: [QUERY_KEYS.QUEST_BOARD, selectedDate],
    queryFn: () => getAssignedQuests(selectedDate, userIds),
    select: (data) => sortByRoutine(data),
    enabled: !!family,
  })
}

export const useAllQuestsQuery = () => {
  const { data: family } = useFamilyQuery()
  const userIds = family?.profiles?.map((profile) => profile.user.id) || []

  return useQuery<QuestSeries[]>({
    queryKey: [QUERY_KEYS.ALL_QUESTS],
    queryFn: () => getOpenQuests(userIds),
    enabled: !!family,
  })
}

export const useQuestReviewQuery = () => {
  const { data: family } = useFamilyQuery()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)

  return useQuery<QuestInstance[]>({
    queryKey: [QUERY_KEYS.QUEST_REVIEW],
    queryFn: () => getQuestsToReview(children as FamilyChildProfile[]),
    enabled: !!family,
  })
}

export const useQuestLogQuery = (seriesId: number) => {
  return useQuery({
    queryKey: [QUERY_KEYS.QUEST_LOG, seriesId],
    queryFn: () => getQuestLog({ seriesId }),
    enabled: !!seriesId,
  })
}

export const useParentTaskQuery = (args?: { enabled: boolean }) => {
  const { user } = useUserQuery()
  const todaysDate = dayjs().format("YYYY-MM-DD")

  return useQuery<QuestInstance[]>({
    queryKey: [QUERY_KEYS.PARENT_TASKS, user?.id],
    queryFn: () => getAssignedQuests(todaysDate, [user?.id as number]),
    enabled: args?.enabled !== false && !!user,
  })
}

export const useParentTaskInstancesQuery = ({
  seriesId,
  minDate,
  maxDate,
  status,
}: {
  seriesId?: number
  minDate?: string
  maxDate?: string
  status?: string
}) => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.PARENT_TASK_INSTANCES, seriesId, user?.id, status],
    queryFn: () =>
      getParentTaskInstances({
        userId: user?.id as number,
        seriesId,
        minDate,
        maxDate,
        status,
      }),
    select: (data) => data.flat(),
    enabled: !!user,
  })
}

export const useIncidentTemplatesQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.INCIDENT_TEMPLATES],
    queryFn: () => getIncidents(),
    enabled: !!user,
  })
}

export const useTrainingsQuery = (
  status = ParentTrainingStatus.IN_PROGRESS
) => {
  const { user } = useUserQuery()

  return useQuery({
    queryKey: [QUERY_KEYS.TRAININGS, status],
    queryFn: () =>
      getTrainings({
        userId: user?.id || 0,
        status,
      }),
    enabled: !!user,
  })
}

export const useNotificationPreferencesQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.NOTIFICATION_PREFERENCES],
    queryFn: () => getNotificationPreferences(user?.id as number),
    enabled: !!user,
  })
}

export const useGetMeTabCardQuery = () => {
  const { user } = useUserQuery()
  const { hasCoach } = useCareTeam()
  const mondayOfWeek = getMondayOfWeek()
  const lastSeenNewRoutinesOverlay = localStorage.getItem(
    localStorageItems.lastSeenNewRoutinesOverlay
  )
  const needsToSeeNewRoutinesOverlay =
    !lastSeenNewRoutinesOverlay ||
    dayjs(lastSeenNewRoutinesOverlay).isBefore(mondayOfWeek)

  return useQuery({
    queryKey: [QUERY_KEYS.ME_TAB_CARD, !needsToSeeNewRoutinesOverlay],
    queryFn: () =>
      getMeTabCard({ hasSeenRoutineFlag: !needsToSeeNewRoutinesOverlay }),
    enabled: !!user && hasCoach,
  })
}

export const useCampaignPlansQuery = () => {
  const { user } = useUserQuery()
  const campaign = user?.sub_offering || "default"
  return useQuery({
    queryKey: [QUERY_KEYS.CAMPAIGN_PLANS, campaign],
    queryFn: () => getCampaignPlans(campaign),
  })
}
